/**
 * Compact Vehicle Year/Make/Model Dataset
 *
 * This is a curated subset of common vehicles for the guided dropdown feature.
 * Covers major manufacturers and popular models from 1986-present.
 *
 * Structure:
 * {
 *   [year]: {
 *     [make]: {
 *       [model]: ["engine1", "engine2", ...]
 *     }
 *   }
 * }
 */

export interface VehicleData {
  [year: string]: {
    [make: string]: {
      [model: string]: string[]; // engine descriptions
    };
  };
}

// Generate years dynamically (current year down to 1986)
const currentYear = new Date().getFullYear();
const startYear = 1986;

// Compact dataset with popular vehicles
// Format: Year -> Make -> Model -> Engine options
export const vehicleData: VehicleData = {
  // Template structure - populated below
};

export interface FallbackModelDefinition {
  // US-market production ranges (inclusive); null end = still in production.
  // A model can have multiple ranges when it was discontinued and revived.
  years: Array<[number, number | null]>;
  engines: string[];
}

// Common model/engine combinations by make.
// NOTE: this object literal must stay JSON-parseable (quoted keys, no
// computed values) — scripts/seed-vehicle-catalog.ps1 extracts and parses it.
const makeModels: { [make: string]: { [model: string]: FallbackModelDefinition } } = {
  "Toyota": {
    "Camry": { "years": [[1986, null]], "engines": ["2.5L 4Cyl", "3.5L V6", "2.5L Hybrid"] },
    "Corolla": { "years": [[1986, null]], "engines": ["1.8L 4Cyl", "2.0L 4Cyl", "1.8L Hybrid"] },
    "RAV4": { "years": [[1996, null]], "engines": ["2.5L 4Cyl", "2.5L Hybrid", "2.5L Prime"] },
    "Highlander": { "years": [[2001, null]], "engines": ["3.5L V6", "2.5L Hybrid"] },
    "Tacoma": { "years": [[1995, null]], "engines": ["2.7L 4Cyl", "3.5L V6"] },
    "Tundra": { "years": [[2000, null]], "engines": ["3.5L V6 TT", "3.5L V6 TT Hybrid", "5.7L V8"] },
    "4Runner": { "years": [[1986, null]], "engines": ["4.0L V6"] },
    "Prius": { "years": [[2001, null]], "engines": ["1.8L Hybrid", "2.0L Hybrid"] },
    "Sienna": { "years": [[1998, null]], "engines": ["2.5L Hybrid", "3.5L V6"] },
  },
  "Honda": {
    "Accord": { "years": [[1986, null]], "engines": ["1.5L Turbo 4Cyl", "2.0L Turbo 4Cyl", "2.0L Hybrid"] },
    "Civic": { "years": [[1986, null]], "engines": ["2.0L 4Cyl", "1.5L Turbo 4Cyl", "2.0L Hybrid"] },
    "CR-V": { "years": [[1997, null]], "engines": ["1.5L Turbo 4Cyl", "2.0L Hybrid"] },
    "Pilot": { "years": [[2003, null]], "engines": ["3.5L V6"] },
    "Odyssey": { "years": [[1995, null]], "engines": ["3.5L V6"] },
    "Ridgeline": { "years": [[2006, null]], "engines": ["3.5L V6"] },
    "HR-V": { "years": [[2016, null]], "engines": ["2.0L 4Cyl", "1.8L 4Cyl"] },
  },
  "Ford": {
    "F-150": { "years": [[1986, null]], "engines": ["2.7L V6 EcoBoost", "3.5L V6 EcoBoost", "5.0L V8", "3.5L PowerBoost Hybrid", "3.0L Diesel"] },
    "F-250": { "years": [[1986, null]], "engines": ["6.2L V8", "7.3L V8", "6.7L Diesel"] },
    "Escape": { "years": [[2001, null]], "engines": ["1.5L EcoBoost", "2.0L EcoBoost", "2.5L Hybrid"] },
    "Explorer": { "years": [[1991, null]], "engines": ["2.3L EcoBoost", "3.0L EcoBoost", "3.3L Hybrid"] },
    "Edge": { "years": [[2007, null]], "engines": ["2.0L EcoBoost", "2.7L V6 EcoBoost"] },
    "Mustang": { "years": [[1986, null]], "engines": ["2.3L EcoBoost", "5.0L V8", "5.2L V8"] },
    "Bronco": { "years": [[1986, 1996], [2021, null]], "engines": ["2.3L EcoBoost", "2.7L V6 EcoBoost"] },
    "Ranger": { "years": [[1986, 2011], [2019, null]], "engines": ["2.3L EcoBoost"] },
    "Maverick": { "years": [[2022, null]], "engines": ["2.5L Hybrid", "2.0L EcoBoost"] },
  },
  "Chevrolet": {
    "Silverado 1500": { "years": [[1999, null]], "engines": ["2.7L Turbo", "5.3L V8", "6.2L V8", "3.0L Diesel"] },
    "Silverado 2500": { "years": [[1999, null]], "engines": ["6.6L V8", "6.6L Diesel"] },
    "Equinox": { "years": [[2005, null]], "engines": ["1.5L Turbo", "1.6L Turbo Diesel"] },
    "Traverse": { "years": [[2009, null]], "engines": ["3.6L V6"] },
    "Tahoe": { "years": [[1995, null]], "engines": ["5.3L V8", "6.2L V8", "3.0L Diesel"] },
    "Suburban": { "years": [[1986, null]], "engines": ["5.3L V8", "6.2L V8", "3.0L Diesel"] },
    "Camaro": { "years": [[1986, 2002], [2010, 2024]], "engines": ["2.0L Turbo", "3.6L V6", "6.2L V8"] },
    "Malibu": { "years": [[1997, null]], "engines": ["1.5L Turbo", "2.0L Turbo"] },
    "Trailblazer": { "years": [[2002, 2009], [2021, null]], "engines": ["1.2L Turbo", "1.3L Turbo"] },
    "Colorado": { "years": [[2004, null]], "engines": ["2.7L Turbo", "3.6L V6", "2.8L Diesel"] },
  },
  "Nissan": {
    "Altima": { "years": [[1993, null]], "engines": ["2.5L 4Cyl", "2.0L VC-Turbo"] },
    "Sentra": { "years": [[1986, null]], "engines": ["2.0L 4Cyl", "1.8L 4Cyl"] },
    "Rogue": { "years": [[2008, null]], "engines": ["2.5L 4Cyl", "1.5L VC-Turbo"] },
    "Murano": { "years": [[2003, null]], "engines": ["3.5L V6"] },
    "Pathfinder": { "years": [[1986, null]], "engines": ["3.5L V6"] },
    "Frontier": { "years": [[1998, null]], "engines": ["3.8L V6", "4.0L V6"] },
    "Titan": { "years": [[2004, 2024]], "engines": ["5.6L V8"] },
    "Armada": { "years": [[2004, null]], "engines": ["5.6L V8"] },
    "Maxima": { "years": [[1986, 2023]], "engines": ["3.5L V6"] },
    "Versa": { "years": [[2007, null]], "engines": ["1.6L 4Cyl"] },
  },
  "Hyundai": {
    "Elantra": { "years": [[1992, null]], "engines": ["2.0L 4Cyl", "1.6L Turbo", "1.6L Hybrid"] },
    "Sonata": { "years": [[1989, null]], "engines": ["2.5L 4Cyl", "1.6L Turbo", "2.0L Hybrid"] },
    "Tucson": { "years": [[2005, null]], "engines": ["2.5L 4Cyl", "1.6L Turbo Hybrid", "1.6L Turbo Plug-in"] },
    "Santa Fe": { "years": [[2001, null]], "engines": ["2.5L 4Cyl", "2.5L Turbo", "1.6L Turbo Hybrid"] },
    "Palisade": { "years": [[2020, null]], "engines": ["3.8L V6"] },
    "Kona": { "years": [[2018, null]], "engines": ["2.0L 4Cyl", "1.6L Turbo", "1.6L Hybrid"] },
    "Venue": { "years": [[2020, null]], "engines": ["1.6L 4Cyl"] },
    "Ioniq 5": { "years": [[2022, null]], "engines": ["Electric"] },
    "Ioniq 6": { "years": [[2023, null]], "engines": ["Electric"] },
  },
  "Kia": {
    "Forte": { "years": [[2010, 2024]], "engines": ["2.0L 4Cyl", "1.6L Turbo"] },
    "K5": { "years": [[2021, null]], "engines": ["1.6L Turbo", "2.5L Turbo"] },
    "Sportage": { "years": [[1995, null]], "engines": ["2.5L 4Cyl", "1.6L Turbo Hybrid", "1.6L Turbo Plug-in"] },
    "Sorento": { "years": [[2003, null]], "engines": ["2.5L 4Cyl", "2.5L Turbo", "1.6L Turbo Hybrid", "1.6L Turbo Plug-in"] },
    "Telluride": { "years": [[2020, null]], "engines": ["3.8L V6"] },
    "Soul": { "years": [[2010, null]], "engines": ["2.0L 4Cyl", "1.6L Turbo"] },
    "Seltos": { "years": [[2021, null]], "engines": ["2.0L 4Cyl", "1.6L Turbo"] },
    "Carnival": { "years": [[2022, null]], "engines": ["3.5L V6"] },
    "EV6": { "years": [[2022, null]], "engines": ["Electric"] },
  },
  "Subaru": {
    "Outback": { "years": [[1995, null]], "engines": ["2.5L 4Cyl", "2.4L Turbo", "3.6L 6Cyl"] },
    "Forester": { "years": [[1998, null]], "engines": ["2.5L 4Cyl", "2.0L Turbo", "2.5L Hybrid"] },
    "Crosstrek": { "years": [[2013, null]], "engines": ["2.0L 4Cyl", "2.5L 4Cyl", "2.0L Hybrid"] },
    "Impreza": { "years": [[1993, null]], "engines": ["2.0L 4Cyl", "2.5L 4Cyl"] },
    "Legacy": { "years": [[1990, null]], "engines": ["2.5L 4Cyl", "2.4L Turbo"] },
    "Ascent": { "years": [[2019, null]], "engines": ["2.4L Turbo"] },
    "WRX": { "years": [[2002, null]], "engines": ["2.0L Turbo", "2.4L Turbo"] },
  },
  "Mazda": {
    "Mazda3": { "years": [[2004, null]], "engines": ["2.5L 4Cyl", "2.5L Turbo", "2.0L 4Cyl"] },
    "Mazda6": { "years": [[2003, 2021]], "engines": ["2.5L 4Cyl", "2.5L Turbo"] },
    "CX-5": { "years": [[2013, null]], "engines": ["2.5L 4Cyl", "2.5L Turbo"] },
    "CX-50": { "years": [[2023, null]], "engines": ["2.5L 4Cyl", "2.5L Turbo"] },
    "CX-30": { "years": [[2020, null]], "engines": ["2.5L 4Cyl", "2.5L Turbo"] },
    "CX-9": { "years": [[2007, 2023]], "engines": ["2.5L Turbo"] },
    "MX-5 Miata": { "years": [[1990, null]], "engines": ["2.0L 4Cyl", "1.5L 4Cyl"] },
  },
  "Jeep": {
    "Wrangler": { "years": [[1987, null]], "engines": ["3.6L V6", "2.0L Turbo", "3.0L Diesel", "4xe Plug-in", "6.4L V8"] },
    "Grand Cherokee": { "years": [[1993, null]], "engines": ["3.6L V6", "5.7L V8", "2.0L Turbo 4xe", "4xe Plug-in", "6.4L V8"] },
    "Cherokee": { "years": [[1986, 2001], [2014, 2023]], "engines": ["2.4L 4Cyl", "3.2L V6", "2.0L Turbo"] },
    "Compass": { "years": [[2007, null]], "engines": ["2.4L 4Cyl", "2.0L Turbo"] },
    "Renegade": { "years": [[2015, 2023]], "engines": ["2.4L 4Cyl", "1.3L Turbo"] },
    "Gladiator": { "years": [[2020, null]], "engines": ["3.6L V6", "3.0L Diesel"] },
    "Wagoneer": { "years": [[1986, 1991], [2022, null]], "engines": ["5.7L V8 eTorque", "6.4L V8"] },
  },
  "BMW": {
    "3 Series": { "years": [[1986, null]], "engines": ["2.0L Turbo 4Cyl", "3.0L Turbo 6Cyl"] },
    "5 Series": { "years": [[1986, null]], "engines": ["2.0L Turbo 4Cyl", "3.0L Turbo 6Cyl", "4.4L V8"] },
    "X3": { "years": [[2004, null]], "engines": ["2.0L Turbo 4Cyl", "3.0L Turbo 6Cyl"] },
    "X5": { "years": [[2000, null]], "engines": ["3.0L Turbo 6Cyl", "4.4L V8", "3.0L Diesel"] },
    "X1": { "years": [[2013, null]], "engines": ["2.0L Turbo 4Cyl"] },
    "X7": { "years": [[2019, null]], "engines": ["3.0L Turbo 6Cyl", "4.4L V8"] },
  },
  "Mercedes-Benz": {
    "C-Class": { "years": [[1994, null]], "engines": ["2.0L Turbo 4Cyl", "3.0L Turbo 6Cyl"] },
    "E-Class": { "years": [[1986, null]], "engines": ["2.0L Turbo 4Cyl", "3.0L Turbo 6Cyl"] },
    "GLC": { "years": [[2016, null]], "engines": ["2.0L Turbo 4Cyl"] },
    "GLE": { "years": [[2016, null]], "engines": ["2.0L Turbo 4Cyl", "3.0L Turbo 6Cyl"] },
    "GLS": { "years": [[2017, null]], "engines": ["3.0L Turbo 6Cyl", "4.0L V8"] },
    "A-Class": { "years": [[2019, 2022]], "engines": ["2.0L Turbo 4Cyl"] },
  },
  "Lexus": {
    "ES": { "years": [[1990, null]], "engines": ["2.5L 4Cyl", "3.5L V6", "2.5L Hybrid"] },
    "RX": { "years": [[1999, null]], "engines": ["3.5L V6", "2.5L Hybrid", "2.4L Turbo 4Cyl"] },
    "NX": { "years": [[2015, null]], "engines": ["2.5L 4Cyl", "2.5L Hybrid", "2.4L Turbo"] },
    "IS": { "years": [[2001, null]], "engines": ["2.0L Turbo", "3.5L V6", "5.0L V8"] },
    "GX": { "years": [[2003, null]], "engines": ["4.6L V8", "3.4L V6 TT"] },
    "LX": { "years": [[1996, null]], "engines": ["5.7L V8", "3.4L V6 TT Hybrid"] },
  },
  "Acura": {
    "TLX": { "years": [[2015, null]], "engines": ["2.0L Turbo", "3.0L V6 Turbo"] },
    "RDX": { "years": [[2007, null]], "engines": ["2.0L Turbo"] },
    "MDX": { "years": [[2001, null]], "engines": ["3.5L V6", "3.0L V6 Hybrid"] },
    "Integra": { "years": [[1986, 2001], [2023, null]], "engines": ["1.5L Turbo"] },
  },
  "Audi": {
    "A4": { "years": [[1996, null]], "engines": ["2.0L Turbo 4Cyl"] },
    "A6": { "years": [[1995, null]], "engines": ["2.0L Turbo 4Cyl", "3.0L V6 Turbo"] },
    "Q5": { "years": [[2009, null]], "engines": ["2.0L Turbo 4Cyl", "2.0L Turbo Plug-in"] },
    "Q7": { "years": [[2007, null]], "engines": ["3.0L V6", "2.0L Turbo 4Cyl"] },
    "Q3": { "years": [[2015, null]], "engines": ["2.0L Turbo 4Cyl"] },
  },
  "Volkswagen": {
    "Jetta": { "years": [[1986, null]], "engines": ["1.4L Turbo", "2.0L Turbo"] },
    "Passat": { "years": [[1990, 2022]], "engines": ["2.0L Turbo"] },
    "Golf": { "years": [[1986, null]], "engines": ["1.4L Turbo", "2.0L Turbo"] },
    "Tiguan": { "years": [[2009, null]], "engines": ["2.0L Turbo"] },
    "Atlas": { "years": [[2018, null]], "engines": ["2.0L Turbo", "3.6L V6"] },
    "Taos": { "years": [[2022, null]], "engines": ["1.5L Turbo"] },
    "ID.4": { "years": [[2021, null]], "engines": ["Electric"] },
  },
  "Volvo": {
    "XC60": { "years": [[2010, null]], "engines": ["2.0L Turbo 4Cyl", "2.0L Turbo Plug-in"] },
    "XC90": { "years": [[2003, null]], "engines": ["2.0L Turbo 4Cyl", "2.0L Turbo Plug-in"] },
    "XC40": { "years": [[2019, null]], "engines": ["2.0L Turbo 4Cyl", "Electric"] },
    "S60": { "years": [[2001, null]], "engines": ["2.0L Turbo 4Cyl", "2.0L Turbo Plug-in"] },
    "S90": { "years": [[2017, null]], "engines": ["2.0L Turbo 4Cyl", "2.0L Turbo Plug-in"] },
  },
  "GMC": {
    "Sierra 1500": { "years": [[1999, null]], "engines": ["2.7L Turbo", "5.3L V8", "6.2L V8", "3.0L Diesel"] },
    "Sierra 2500": { "years": [[1999, null]], "engines": ["6.6L V8", "6.6L Diesel"] },
    "Terrain": { "years": [[2010, null]], "engines": ["1.5L Turbo", "2.0L Turbo"] },
    "Acadia": { "years": [[2007, null]], "engines": ["2.0L Turbo", "3.6L V6"] },
    "Yukon": { "years": [[1992, null]], "engines": ["5.3L V8", "6.2L V8", "3.0L Diesel"] },
  },
  "Ram": {
    "1500": { "years": [[2011, null]], "engines": ["3.6L V6", "5.7L V8", "3.0L Diesel", "6.2L Supercharged V8"] },
    "2500": { "years": [[2011, null]], "engines": ["6.4L V8", "6.7L Diesel"] },
    "3500": { "years": [[2011, null]], "engines": ["6.4L V8", "6.7L Diesel"] },
    "ProMaster": { "years": [[2014, null]], "engines": ["3.6L V6"] },
  },
  "Dodge": {
    "Charger": { "years": [[2006, 2023]], "engines": ["3.6L V6", "5.7L V8", "6.4L V8", "6.2L Supercharged V8"] },
    "Challenger": { "years": [[2008, 2023]], "engines": ["3.6L V6", "5.7L V8", "6.4L V8", "6.2L Supercharged V8"] },
    "Durango": { "years": [[1998, null]], "engines": ["3.6L V6", "5.7L V8", "6.4L V8"] },
    "Hornet": { "years": [[2023, null]], "engines": ["2.0L Turbo", "1.3L Turbo Plug-in"] },
  },
  "Chrysler": {
    "Pacifica": { "years": [[2017, null]], "engines": ["3.6L V6", "3.6L V6 Plug-in"] },
    "300": { "years": [[2005, 2023]], "engines": ["3.6L V6", "5.7L V8"] },
  },
  "Buick": {
    "Encore": { "years": [[2013, 2022]], "engines": ["1.4L Turbo"] },
    "Encore GX": { "years": [[2020, null]], "engines": ["1.2L Turbo", "1.3L Turbo"] },
    "Envision": { "years": [[2016, null]], "engines": ["2.0L Turbo", "2.5L 4Cyl"] },
    "Enclave": { "years": [[2008, null]], "engines": ["3.6L V6"] },
  },
  "Cadillac": {
    "XT4": { "years": [[2019, null]], "engines": ["2.0L Turbo"] },
    "XT5": { "years": [[2017, null]], "engines": ["2.0L Turbo", "3.6L V6"] },
    "XT6": { "years": [[2020, null]], "engines": ["2.0L Turbo", "3.6L V6"] },
    "Escalade": { "years": [[1999, null]], "engines": ["6.2L V8", "3.0L Diesel"] },
    "CT4": { "years": [[2020, null]], "engines": ["2.0L Turbo", "3.6L V6"] },
    "CT5": { "years": [[2020, null]], "engines": ["2.0L Turbo", "3.0L V6 TT", "6.2L V8"] },
    "Lyriq": { "years": [[2023, null]], "engines": ["Electric"] },
  },
  "Lincoln": {
    "Corsair": { "years": [[2020, null]], "engines": ["2.0L Turbo", "2.5L Hybrid", "2.3L Turbo"] },
    "Nautilus": { "years": [[2019, null]], "engines": ["2.0L Turbo", "2.7L V6 TT"] },
    "Aviator": { "years": [[2003, 2005], [2020, null]], "engines": ["3.0L V6 TT", "3.0L V6 TT Hybrid"] },
    "Navigator": { "years": [[1998, null]], "engines": ["3.5L V6 TT"] },
  },
  "Infiniti": {
    "Q50": { "years": [[2014, null]], "engines": ["2.0L Turbo", "3.0L V6 TT"] },
    "Q60": { "years": [[2014, 2022]], "engines": ["2.0L Turbo", "3.0L V6 TT"] },
    "QX50": { "years": [[2014, null]], "engines": ["2.0L VC-Turbo"] },
    "QX60": { "years": [[2014, null]], "engines": ["3.5L V6"] },
    "QX80": { "years": [[2014, null]], "engines": ["5.6L V8"] },
  },
  "Mitsubishi": {
    "Outlander": { "years": [[2003, null]], "engines": ["2.5L 4Cyl", "2.4L Hybrid"] },
    "Eclipse Cross": { "years": [[2018, null]], "engines": ["1.5L Turbo"] },
    "Mirage": { "years": [[1986, 2002], [2014, null]], "engines": ["1.2L 3Cyl"] },
  },
  "Tesla": {
    "Model 3": { "years": [[2017, null]], "engines": ["Electric"] },
    "Model Y": { "years": [[2020, null]], "engines": ["Electric"] },
    "Model S": { "years": [[2012, null]], "engines": ["Electric"] },
    "Model X": { "years": [[2016, null]], "engines": ["Electric"] },
    "Cybertruck": { "years": [[2024, null]], "engines": ["Electric"] },
  },
  "Porsche": {
    "911": { "years": [[1986, null]], "engines": ["3.0L TT Flat-6", "3.8L TT Flat-6", "4.0L Flat-6"] },
    "Cayenne": { "years": [[2003, null]], "engines": ["3.0L V6 Turbo", "2.9L V6 TT", "4.0L V8 TT"] },
    "Macan": { "years": [[2015, null]], "engines": ["2.0L Turbo", "2.9L V6 TT", "3.0L V6 Turbo"] },
    "Panamera": { "years": [[2010, null]], "engines": ["2.9L V6 TT", "4.0L V8 TT"] },
    "Taycan": { "years": [[2020, null]], "engines": ["Electric"] },
  },
  "Genesis": {
    "G70": { "years": [[2019, null]], "engines": ["2.0L Turbo", "3.3L V6 TT"] },
    "G80": { "years": [[2017, null]], "engines": ["2.5L Turbo", "3.5L V6 TT", "2.5L 4Cyl"] },
    "G90": { "years": [[2017, null]], "engines": ["3.5L V6 TT", "5.0L V8"] },
    "GV70": { "years": [[2022, null]], "engines": ["2.5L Turbo", "3.5L V6 TT"] },
    "GV80": { "years": [[2021, null]], "engines": ["2.5L Turbo", "3.5L V6 TT"] },
  },
  "Mini": {
    "Cooper": { "years": [[2002, null]], "engines": ["1.5L Turbo 3Cyl", "2.0L Turbo 4Cyl"] },
    "Cooper S": { "years": [[2002, null]], "engines": ["2.0L Turbo 4Cyl"] },
    "Countryman": { "years": [[2011, null]], "engines": ["1.5L Turbo", "2.0L Turbo", "1.5L Hybrid"] },
  },
};

function isModelActiveInYear(definition: FallbackModelDefinition, year: number): boolean {
  return definition.years.some(
    ([rangeStart, rangeEnd]) => year >= rangeStart && (rangeEnd === null || year <= rangeEnd)
  );
}

// Populate vehicleData for all years from startYear to currentYear
for (let year = startYear; year <= currentYear; year++) {
  const yearData: VehicleData[string] = {};

  for (const make of Object.keys(makeModels)) {
    const models: { [model: string]: string[] } = {};

    for (const [model, definition] of Object.entries(makeModels[make])) {
      if (!isModelActiveInYear(definition, year)) continue;

      const engines = definition.engines;
      // Filter engine options by year (simplified logic)
      // Earlier years have fewer engine options
      const yearIndex = year - startYear;
      const maxEngines = Math.min(engines.length, Math.max(1, Math.ceil((yearIndex + 5) / 10)));

      // For newer vehicles, include all engines
      // For older vehicles, progressively add more options
      models[model] = year >= 2015 ? engines : engines.slice(0, maxEngines);
    }

    if (Object.keys(models).length > 0) {
      yearData[make] = models;
    }
  }

  vehicleData[year] = yearData;
}

// Helper functions for accessing vehicle data
export function getYears(): number[] {
  return Object.keys(vehicleData).map(Number).sort((a, b) => b - a);
}

export function getMakes(year: number): string[] {
  const yearData = vehicleData[year];
  return yearData ? Object.keys(yearData).sort() : [];
}

export function getModels(year: number, make: string): string[] {
  const yearData = vehicleData[year];
  if (!yearData) return [];

  const makeData = yearData[make];
  return makeData ? Object.keys(makeData).sort() : [];
}

export function getEngines(year: number, make: string, model: string): string[] {
  const yearData = vehicleData[year];
  if (!yearData) return [];

  const makeData = yearData[make];
  if (!makeData) return [];

  return makeData[model] || [];
}

// Parse engine string to extract displacement and cylinders
export interface ParsedEngine {
  displacementL: number | null;
  cylinders: number | null;
  description: string;
  isElectric: boolean;
  isHybrid: boolean;
  isTurbo: boolean;
  isDiesel: boolean;
}

export function parseEngine(engineStr: string): ParsedEngine {
  const lowerStr = engineStr.toLowerCase();

  // Check for electric
  const isElectric = lowerStr.includes('electric');

  // Check for hybrid
  const isHybrid = lowerStr.includes('hybrid') || lowerStr.includes('e-torque');

  // Check for turbo
  const isTurbo = lowerStr.includes('turbo') || lowerStr.includes('tt');

  // Check for diesel
  const isDiesel = lowerStr.includes('diesel');

  // Extract displacement
  let displacementL: number | null = null;
  const displacementMatch = engineStr.match(/(\d+\.?\d*)\s*L/i);
  if (displacementMatch) {
    displacementL = parseFloat(displacementMatch[1]);
  }

  // Extract cylinders
  let cylinders: number | null = null;
  const cylinderMatch = engineStr.match(/(\d+)\s*cyl/i);
  if (cylinderMatch) {
    cylinders = parseInt(cylinderMatch[1], 10);
  } else if (lowerStr.includes('v6')) {
    cylinders = 6;
  } else if (lowerStr.includes('v8')) {
    cylinders = 8;
  } else if (lowerStr.includes('v12')) {
    cylinders = 12;
  } else if (lowerStr.includes('flat-6')) {
    cylinders = 6;
  }

  return {
    displacementL,
    cylinders,
    description: engineStr,
    isElectric,
    isHybrid,
    isTurbo,
    isDiesel,
  };
}

// Generate engine description from components
export function generateEngineDescription(
  displacementL: number | null,
  cylinders: number | null,
  isTurbo: boolean = false,
  isHybrid: boolean = false,
  isElectric: boolean = false
): string {
  if (isElectric) {
    return 'Electric';
  }

  const parts: string[] = [];

  if (displacementL) {
    parts.push(`${displacementL.toFixed(1)}L`);
  }

  if (cylinders) {
    parts.push(`V${cylinders}`);
  }

  if (isTurbo) {
    parts.push('Turbo');
  }

  if (isHybrid) {
    parts.push('Hybrid');
  }

  return parts.join(' ');
}

// Default export for convenience
export default vehicleData;
